import type {
  MachineConfig,
  AnyEventObject,
  InternalMachineOptions,
  EventObject,
  ResolveTypegenMeta,
  BaseActionObject,
  ServiceMap,
  TypegenConstraint,
  TypegenDisabled,
  StateMachine,
  State,
} from 'xstate'
import type { ApiPromise } from '@polkadot/api'
import type { Signer } from '@polkadot/types/types'
import type { SpiritAttributesResponse } from '../spirit/requests'
import type { QueryClient } from '@tanstack/query-core'

import Decimal from 'decimal.js'
import {assign} from 'xstate'
import * as E from 'fp-ts/Either'
import {
  mergeRight,
  mergeDeepRight,
  pathOr,
  cond,
  equals,
  always,
  T,
  values,
  map,
  propOr,
  flatten,
  sum,
} from 'ramda'

import {LevelEnum, RaceEnum, CareerEnum} from '@/types/nftAttribute'
import {NftSaleInfo, RarityType, RaceType} from '@/features/pwNftSale/chain'
import * as Consts from './constants'

//
// Types
//

export interface GuardResultMap {
  canBuyNFT: E.Either<string, null>
  canPreorder: E.Either<string, null>
}

export interface NftSaleContext {
  api?: ApiPromise
  account?: string

  subqlEndpoint: string
  pwApiEndpoint: string
  queryClient: QueryClient

  // @FIXME typing for the unknown.
  spirit?: SpiritAttributesResponse['spirit']
  spiritClaimed: boolean

  // sale stage controlled by chain.
  canPreorderOriginOfShells: boolean
  canPurchaseRareOriginOfShells: boolean
  canPurchasePrimeOriginOfShells: boolean
  lastDayOfSale: boolean

  inWhiteList?: boolean
  hasPreOrdered?: boolean

  // Pha balance for current account
  pha: Decimal

  // @FIXME typing for the unknown.
  ownNFTs: unknown[]

  // iventory level information in double diamental array.
  inventoryLevel?: Record<RarityType, Record<RaceType, NftSaleInfo>>

  // price table
  prices: Record<RarityType, number>

  // composing the NFT skus.
  options: {
    rarity: LevelEnum
    race: RaceEnum
    career: CareerEnum
  }

  // Last error message from any invoke.
  lastError?: string | undefined | null

  // Holding Either object represent can do transition to next state or not.
  guarded?: GuardResultMap

  canMint?: boolean

  signer?: Signer

  salePhase: Consts.SalePhases

  refCode?: string
}

export type INftSaleMachine = StateMachine<NftSaleContext, any, AnyEventObject>

export type INftState = State<
  NftSaleContext,
  AnyEventObject,
  any,
  {
    value: any
    context: NftSaleContext
  },
  ResolveTypegenMeta<TypegenDisabled, AnyEventObject, BaseActionObject, ServiceMap>
>

export interface NftSaleMachineServiceMap extends ServiceMap {
  doInitializing: {
    data: (context: NftSaleContext) => Promise<unknown>
  }
  doBuying: {
    data: (context: NftSaleContext) => Promise<unknown>
  }
  doPreordering: {
    data: (context: NftSaleContext) => Promise<unknown>
  }
}

export type NftSaleMachineInternalMachineOptions<
  TEvent extends EventObject = AnyEventObject,
  TTypesMeta extends TypegenConstraint = TypegenDisabled
> = InternalMachineOptions<
  NftSaleContext,
  TEvent,
  ResolveTypegenMeta<
    TTypesMeta,
    TEvent,
    BaseActionObject,
    NftSaleMachineServiceMap
  >
>

export type NftSaleMachineInternalMachineOptionsOverrides =
  Partial<NftSaleMachineInternalMachineOptions> &
    Pick<NftSaleMachineInternalMachineOptions, 'services'>

//
// Internal Functions.
//

const countOwnedNFTs = ({ownNFTs}: NftSaleContext) => {
  return Object.entries(ownNFTs).reduce((p, lst) => p + lst.length, 0)
}

const getRarity = cond([
  [equals(LevelEnum.Common), always('Prime')],
  [equals(LevelEnum.Magic), always('Magic')],
  [equals(LevelEnum.Legendary), always('Legendary')],
  [T, always('')],
])

const getRace = cond([
  [equals(RaceEnum.Cyborg), always('Cyborg')],
  [equals(RaceEnum.AISpectre), always('AISpectre')],
  [equals(RaceEnum.XGene), always('XGene')],
  [equals(RaceEnum.Pandroid), always('Pandroid')],
  [T, always('')],
])

function getGuarded(
  context: NftSaleContext,
  event: AnyEventObject
): GuardResultMap {
  const nextContext: NftSaleContext =
    event.type === 'updateOptions'
      ? mergeDeepRight(context, {options: event.data})
      : mergeDeepRight(context, event.data)
  return {
    canBuyNFT: canBuyNFT(nextContext),
    canPreorder: canPreorder(nextContext),
  }
}

interface SalePhaseDetectorDeps {
  now: () => Number
  sales_begin_time: number
  sales_ended_time: number
}

export function makeSalePhaseDetector(deps: SalePhaseDetectorDeps) {
  const { now, sales_begin_time, sales_ended_time } = deps
  return function getSalePhase(
    ctx: Pick<NftSaleContext, 'canPreorderOriginOfShells' | 'canPurchasePrimeOriginOfShells' | 'canPurchaseRareOriginOfShells' | 'lastDayOfSale'>
  ) {
    const {
      canPreorderOriginOfShells,
      canPurchasePrimeOriginOfShells,
      canPurchaseRareOriginOfShells,
      lastDayOfSale,
    } = ctx
    const ts = now()
    if (
      !canPreorderOriginOfShells &&
      !canPurchasePrimeOriginOfShells &&
      !canPurchaseRareOriginOfShells &&
      !lastDayOfSale
    ) {
      if (ts < sales_begin_time) {
        return Consts.SalePhases.PREPARE
      }
      if (ts > sales_ended_time) {
        return Consts.SalePhases.CLOSED
      }
      return Consts.SalePhases.PREPARE
    }

    if (!lastDayOfSale && canPurchasePrimeOriginOfShells) {
      return Consts.SalePhases.WHITELISTED
    }

    if (!lastDayOfSale && canPreorderOriginOfShells) {
      return Consts.SalePhases.PREORDER
    }

    if (lastDayOfSale) {
      return Consts.SalePhases.LAST_DAY
    }

    if (canPurchaseRareOriginOfShells) {
      return Consts.SalePhases.RELEASE
    }

    return Consts.SalePhases.PREPARE
  }
}

const getSalePhase = makeSalePhaseDetector({
  now: () => Date.now() / 1000,
  sales_begin_time: Consts.SALE_BEGIN_TIME,
  sales_ended_time: Consts.SALE_ENDED_TIME,
})

const canBuyNFT = (context: NftSaleContext) => {
  const {options, pha, prices, inventoryLevel} = context
  const rarity = getRarity(options.rarity)
  const race = getRace(options.race)
  if (!options.career || !race || !rarity) {
    return E.left('')
  }
  if (!context.spiritClaimed) {
    // @FIXME ask for new copywriting.
    return E.left('Please claim your spirit first')
  }
  const k = rarity === 'Prime' ? LevelEnum.Common : rarity
  // Has enought balance to continue?
  const hasEnoughtPha = pha.greaterThan(new Decimal(prices[k] || 0))
  // Any iventory level available?
  const hasInventory = inventoryLevel
    ? pathOr(0, [rarity, race, 'raceForSaleCount'], inventoryLevel) > 0
    : false
  // Has been owned at least one NFT before.
  const owned = countOwnedNFTs(context)
  //
  if (context.lastDayOfSale && hasEnoughtPha && hasInventory) {
    if (rarity === 'Prime') {
      return context.canPurchasePrimeOriginOfShells
        ? E.right(null)
        : E.left('Not yet available for sale')
    } else {
      return context.canPurchaseRareOriginOfShells
        ? E.right(null)
        : E.left('Not yet available for sale')
    }
  }
  if (!context.lastDayOfSale && owned) {
    return E.left('Limit to one mint per wallet')
  }
  if (!hasInventory) {
    return E.left('Max supply reached')
  }
  if (
    !context.canPreorderOriginOfShells &&
    !context.canPurchaseRareOriginOfShells &&
    !context.canPurchasePrimeOriginOfShells
  ) {
    return E.left('Not yet available for sale')
  }
  if (
    !context.canPurchasePrimeOriginOfShells &&
    options.rarity === LevelEnum.Common
  ) {
    return E.left('Not yet available for sale')
  }
  if (
    !context.canPreorderOriginOfShells &&
    context.canPurchasePrimeOriginOfShells &&
    !context.inWhiteList &&
    rarity === 'Prime'
  ) {
    return E.left('Not yet available for sale')
  }
  if (!hasEnoughtPha) {
    return E.left('Insufficient balance')
  }
  if (context.canPurchasePrimeOriginOfShells && context.inWhiteList) {
    return E.right(null)
  }
  if (!context.lastDayOfSale && context.canPreorderOriginOfShells) {
    // NOTE: these two flags can not enable at the same time.
    if (
      context.canPurchaseRareOriginOfShells &&
      options.rarity !== LevelEnum.Common
    ) {
      return E.right(null)
    }
    return E.left('')
  }
  if (
    context.canPurchaseRareOriginOfShells &&
    options.rarity !== LevelEnum.Common
  ) {
    return E.right(null)
  }
  return E.left('')
}

const canPreorder = (context: NftSaleContext) => {
  const {options, pha, prices} = context
  const rarity = getRarity(options.rarity)
  const k = rarity === 'Prime' ? LevelEnum.Common : rarity
  const hasEnoughtPha = pha.greaterThan(new Decimal(prices[k] || 0))
  if (
    context.canPreorderOriginOfShells &&
    !context.canPurchasePrimeOriginOfShells &&
    context.options.rarity === LevelEnum.Common &&
    context.ownNFTs.length === 0 &&
    hasEnoughtPha &&
    ((!context.lastDayOfSale && !context.hasPreOrdered) ||
      context.lastDayOfSale)
  ) {
    return E.right(null)
  }
  if (!context.spiritClaimed) {
    // @FIXME ask for new copywriting.
    return E.left('Please claim your spirit first')
  }
  if (!context.lastDayOfSale && context.hasPreOrdered) {
    // @FIXME ask for new copywriting.
    return E.left('You has been proorder successfully before')
  }
  if (context.lastDayOfSale) {
    return E.right(null)
  }
  if (
    !context.canPreorderOriginOfShells ||
    context.canPurchasePrimeOriginOfShells
  ) {
    return E.left('Not yet available for sale')
  }
  if (!hasEnoughtPha) {
    return E.left('Insufficient balance')
  }
  if (context.ownNFTs.length > 0) {
    return E.left('Limit to one mint per wallet')
  }
  return E.left('')
}

const checkCanMint = (context: NftSaleContext) => {
  const {
    spiritClaimed,
    canPreorderOriginOfShells,
    canPurchaseRareOriginOfShells,
    canPurchasePrimeOriginOfShells,
  } = context
  return (
    spiritClaimed &&
    (canPreorderOriginOfShells ||
      canPurchaseRareOriginOfShells ||
      canPurchasePrimeOriginOfShells)
  )
}

//
// State Machine Factory Functions.
//

export function createNftSaleMachineConfig(
  context: NftSaleContext
): MachineConfig<NftSaleContext, any, AnyEventObject> {
  return {
    id: 'nftSaleMachine',
    context,
    predictableActionArguments: true,
    initial: 'initializing',
    states: {
      initializing: {
        invoke: {
          id: 'initializing',
          src: 'doInitializing',
          onDone: {
            target: 'initialized',
            actions: ['updateInternalState', 'updateInternalCompoundState'],
          },
          onError: {
            target: 'initialized',
            actions: ['saveLastError'],
          },
        },
        meta: {
          loading: true,
        },
      },
      initialized: {
        always: [
          {target: 'newbie', cond: 'isNewbie'},
          {target: 'spiritClaimed', cond: 'hasClaimed'},
        ],
      },
      buying: {
        invoke: {
          id: 'buying',
          src: 'doBuying',
          onDone: {
            target: 'succeed',
          },
          onError: {
            target: 'failure',
            actions: ['saveLastError'],
          },
        },
      },
      preordering: {
        invoke: {
          id: 'preordering',
          src: 'doPreordering',
          onDone: {
            target: 'succeed',
            actions: ['setPreorderFlag'],
          },
          onError: {
            target: 'failure',
            actions: ['saveLastError'],
          },
        },
      },
      // For UI, so we can use useEffect to monitor the state and did post-process the result.
      succeed: {
        after: {
          100: {target: 'initializing'},
        },
      },
      failure: {
        after: {
          100: {target: 'initializing'},
        },
      },
      newbie: {
        on: {
          claim: 'spiritClaimed',
          reinitialization: 'initializing',
          updateInternalState: {
            target: 'initializing',
            actions: ['updateInternalState', 'updateInternalCompoundState'],
            // actions: ['updateInternalState'],
          },
          updateDependencies: {
            internal: true,
            actions: ['updateDependencies'],
          },
          updateBalance: {
            internal: true,
            actions: ['updateBalance', 'updateInternalCompoundState'],
          },
          updateOptions: {
            internal: true,
            actions: ['updateOptions', 'updateInternalCompoundState'],
          },
        },
      },
      spiritClaimed: {
        on: {
          reinitialization: 'initializing',
          buyNFT: {target: 'buying', cond: 'canBuy'},
          preorder: {target: 'preordering', cond: 'canPreorder'},
          updateInternalState: {
            target: 'initializing',
            actions: ['updateInternalState', 'updateInternalCompoundState'],
          },
          updateDependencies: {
            internal: true,
            actions: ['updateDependencies'],
          },
          updateBalance: {
            internal: true,
            actions: ['updateBalance', 'updateInternalCompoundState'],
          },
          updateOptions: {
            internal: true,
            actions: ['updateOptions', 'updateInternalCompoundState'],
          },
        },
      },
    },
  }
}

export function createNftSaleMachineOptions(
  overrides: NftSaleMachineInternalMachineOptionsOverrides
) {
  return mergeDeepRight(
    {
      guards: {
        isNewbie: (context) => !context.spiritClaimed,
        hasClaimed: (context) => !!context.spiritClaimed,
        haveNoNFT: (context) => countOwnedNFTs(context) === 0,
        ownAtLeastOneNFT: (context) => countOwnedNFTs(context) > 0,
        canBuy: (context) =>
          context.guarded && E.isRight(context.guarded.canBuyNFT),
        canPreorder: (context) =>
          context.guarded && E.isRight(context.guarded.canPreorder),
      },

      actions: {
        updateInternalState: assign({
          spirit: (_, event) => event.data.spirit,
          spiritClaimed: (_, event) => !!event.data.spiritClaimed,
          hasPreOrdered: (_, event) => !!event.data.hasPreOrdered,
          inWhiteList: (_, event) => event.data.inWhiteList,
          ownNFTs: (_, event) => event.data.ownNFTs,
          inventoryLevel: (_, event) => event.data.inventoryLevel,
          canPreorderOriginOfShells: (_, event) =>
            event.data.canPreorderOriginOfShells,
          canPurchaseRareOriginOfShells: (_, event) =>
            event.data.canPurchaseRareOriginOfShells,
          canPurchasePrimeOriginOfShells: (_, event) =>
            event.data.canPurchasePrimeOriginOfShells,
          lastDayOfSale: (_, event) => event.data.lastDayOfSale,
          prices: (_, event) => event.data.prices,
        }),

        updateDependencies: assign({
          api: (_, event) => event.data.api,
          account: (_, event) => event.data.account,
          signer: (_, event) => event.data.signer,
          refCode: (_, event) => event.data.refCode,
        }),

        saveLastError: assign({
          lastError: (_, {data}) => `${data.message}`,
        }),

        updateBalance: assign({
          pha: (_, event) => event.data.pha,
        }),

        updateOptions: assign({
          options: (context: NftSaleContext, event) =>
            mergeRight(context.options, event.data),
        }),

        updateSaleFlags: assign({
          canPreorderOriginOfShells: (_, event) =>
            event.data.canPreorderOriginOfShells,
          canPurchaseRareOriginOfShells: (_, event) =>
            event.data.canPurchaseRareOriginOfShells,
          canPurchasePrimeOriginOfShells: (_, event) =>
            event.data.canPurchasePrimeOriginOfShells,
          lastDayOfSale: (_, event) => event.data.lastDayOfSale,
        }),

        updateInternalCompoundState: assign<NftSaleContext>({
          canMint: (ctx) => checkCanMint(ctx),
          guarded: (ctx: NftSaleContext, event) => getGuarded(ctx, event),
          salePhase: (ctx) => getSalePhase(ctx),
          lastError: () => '',
        }),

        setPreorderFlag: assign({
          hasPreOrdered: () => true,
        }),
      },
    } as Partial<NftSaleMachineInternalMachineOptions>,
    overrides
  ) as NftSaleMachineInternalMachineOptions
}
